import Component from '@glimmer/component';
import { service } from '@ember/service';
import { concat } from '@ember/helper';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import { LinkTo } from '@ember/routing';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import Heading from 'tio-ui/components/heading';
import NavCard from 'tio-ui/components/nav-card';
import StatCard from 'tio-ui/components/stat-card';
import Text from 'tio-ui/components/text';
import style from 'ember-style-modifier/modifiers/style';
import { PhArrowLeft, PhBank, PhCloudArrowUp, PhHandshake, PhTarget } from 'ember-phosphor-icons';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { LoanExtension as LoanModel } from 'tio-data/data/resources/loan.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

export interface LoanAccountDetailModel {
  account: AccountModel | undefined;
  person: PersonModel;
  accountId: string;
}

interface LoanAccountDetailSignature {
  Args: {
    model: LoanAccountDetailModel;
  };
}

type AccountType = 'federal' | 'private' | 'refinanced';

/** Federal, private or refinanced, read from the first loan's class. */
function accountType(loans: LoanModel[]): AccountType {
  const cls = loans[0]?.loanClass?.toLowerCase() || '';
  if (cls.includes('private')) return 'private';
  if (cls.includes('refinance')) return 'refinanced';
  return 'federal';
}

/** Whole-dollar share of a loan already paid down, clamped to 0–100. */
function loanProgressPercent(loan: LoanModel): number {
  if (!loan.originalAmount) return 0;
  const paid = loan.originalAmount - (loan.currentBalance || 0);
  return Math.max(0, Math.min(100, Math.round((paid / loan.originalAmount) * 100)));
}

const lastFour = (value: string | undefined) => (value || '').slice(-4);
const observed = (loan: LoanModel, key: string) =>
  (loan.observation as unknown as Record<string, unknown> | undefined)?.[key];
const loanInterestRate = (loan: LoanModel) => Number(observed(loan, 'interest-rate') || 0);
const principalBalance = (loan: LoanModel) => Number(observed(loan, 'principal-balance') || 0);
const interestBalance = (loan: LoanModel) => Number(observed(loan, 'interest-balance') || 0);

/**
 * One linked loan account in detail: the hero with its balance and standing,
 * the account's key metrics, its payoff progress, one card per loan, and the
 * next steps that apply to it. Replaces the prototype loan-detail page on the shipped route.
 */
export default class LoanAccountDetail extends Component<LoanAccountDetailSignature> {
  @service declare intl: IntlService;

  get account() {
    return this.args.model.account;
  }

  get activeLoans(): LoanModel[] {
    return this.account?.activeLoans || [];
  }

  get servicerName() {
    return this.account?.institutionName || this.intl.t('accounts.loan_detail.default_servicer');
  }

  get totalBalance() {
    return this.activeLoans.reduce((sum, loan) => sum + (loan.currentBalance || 0), 0);
  }

  get totalOriginalBalance() {
    return this.activeLoans.reduce((sum, loan) => sum + (loan.originalAmount || 0), 0);
  }

  get paidDown() {
    return Math.max(
      0,
      Math.min(this.totalOriginalBalance - this.totalBalance, this.totalOriginalBalance)
    );
  }

  get progressPercent() {
    if (!this.totalOriginalBalance) return 0;
    return Math.round((this.paidDown / this.totalOriginalBalance) * 100);
  }

  /** A USD amount in the active locale; `digits` fixes the fraction digits when given. */
  money = (value: number | undefined | null, digits?: number) =>
    this.intl.formatNumber(value || 0, {
      style: 'currency',
      currency: 'USD',
      ...(digits === undefined
        ? {}
        : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    });

  /** A percentage rate to two decimals in the active locale. */
  formatRate = (rate: number | undefined) =>
    this.intl.formatNumber(rate || 0, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  get weightedInterestRate() {
    return this.formatRate(Number(this.account?.loanInterestAverage || 0));
  }

  get monthlyPayment() {
    return this.account?.loanMinMonthlyPayment || this.account?.providerRegularMonthlyPayment || 0;
  }

  get isReceivingContributions() {
    return (
      !!this.account && this.account.id === this.args.model.person.accountReceivingContributionsId
    );
  }

  /** One sentence for the hero: how many active loans, and whether contributions land here. */
  get heroSubtitle() {
    if (!this.activeLoans.length) return this.intl.t('accounts.loan_detail.no_loans');
    const loans = this.intl.t('accounts.loan_detail.hero_subtitle', {
      count: this.activeLoans.length,
      servicer: this.servicerName,
    });
    return this.isReceivingContributions
      ? `${loans} ${this.intl.t('accounts.loan_detail.hero_subtitle_contributions')}`
      : loans;
  }

  get accountType(): AccountType {
    return accountType(this.activeLoans);
  }

  /** The type the servicer reported, else a label for the loan's class. */
  loanTypeName = (loan: LoanModel): string => {
    const reported = observed(loan, 'loan-type') || observed(loan, 'raw-loan-type');
    if (typeof reported === 'string' && reported) return reported;
    const cls = loan.loanClass?.toLowerCase() || '';
    const key = cls.includes('stafford')
      ? 'stafford'
      : cls.includes('plus')
        ? 'plus'
        : cls.includes('private')
          ? 'private'
          : cls.includes('consolidat')
            ? 'consolidation'
            : 'student';
    return this.intl.t(`accounts.loan_detail.loan_types.${key}`);
  };

  loanClassLabel = (loan: LoanModel): string =>
    loan.loanClass || this.intl.t('accounts.loan_detail.unknown');

  <template>
    <VStack class="gap-8" data-test-loan-account-detail>
      <div>
        <LinkTo
          @route="authenticated.account-activity.accounts"
          class="app-back-link"
          data-test-loan-account-detail-back
        >
          <PhArrowLeft @size={{16}} />
          {{t "accounts.loan_detail.back"}}
        </LinkTo>
      </div>

      <PageHero
        @icon={{PhBank}}
        @title={{this.servicerName}}
        @subtitle={{this.heroSubtitle}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      >
        <:stat>
          <div class="tio-text-3xl-bold" data-test-loan-account-detail-hero-balance>
            {{this.money this.totalBalance}}
          </div>
          <Text @size="sm" class="tio-fg-muted">{{t "accounts.loan_detail.current_balance"}}</Text>
        </:stat>
        <:actions>
          {{#if this.isReceivingContributions}}
            <Chip @intent="success" data-test-loan-account-detail-contributions>
              {{t "accounts.loan_detail.receiving_contributions"}}
            </Chip>
          {{/if}}
          <Chip @intent="default">
            {{t "accounts.loan_detail.loan_count" count=this.activeLoans.length}}
          </Chip>
          {{#if this.activeLoans.length}}
            <Chip @intent="default">
              {{t (concat "accounts.loan_detail.account_types." this.accountType)}}
            </Chip>
          {{/if}}
        </:actions>
      </PageHero>

      <Section data-test-loan-account-detail-overview>
        <:header>{{t "accounts.loan_detail.overview"}}</:header>
        <:body>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
            <StatCard
              @label={{t "accounts.loan_detail.total_balance"}}
              @value={{this.money this.totalBalance}}
              @subtext={{t
                "accounts.loan_detail.original_subtext"
                amount=(this.money this.totalOriginalBalance)
              }}
              data-test-loan-account-detail-balance
            />
            <StatCard
              @label={{t "accounts.loan_detail.average_interest_rate"}}
              @value={{concat this.weightedInterestRate "%"}}
              @subtext={{t "accounts.loan_detail.weighted_average"}}
            />
            <StatCard
              @label={{t "accounts.loan_detail.monthly_payment"}}
              @value={{this.money this.monthlyPayment}}
              @subtext={{t "accounts.loan_detail.minimum_required"}}
            />
            <StatCard
              @label={{t "accounts.loan_detail.paid_off"}}
              @value={{concat this.progressPercent "%"}}
              @subtext={{t "accounts.loan_detail.paid_down" amount=(this.money this.paidDown)}}
            />
          </div>
        </:body>
      </Section>

      <Section data-test-loan-account-detail-progress>
        <:header>{{t "accounts.loan_detail.payoff_progress"}}</:header>
        <:subHeader>{{t
            "accounts.loan_detail.percent_complete"
            percent=this.progressPercent
          }}</:subHeader>
        <:body>
          <div
            class="w-full h-2 tio-bg-track rounded-full overflow-hidden flex"
            role="progressbar"
            aria-valuenow={{this.progressPercent}}
            aria-valuemin={{0}}
            aria-valuemax={{100}}
            aria-label={{t "accounts.loan_detail.payoff_progress"}}
          >
            <div
              class="h-full app-progress-fill--brand"
              {{style width=(concat this.progressPercent "%")}}
            ></div>
          </div>
          <div class="tio-row-between flex-wrap gap-2 tio-text-xs-medium tio-fg-muted mt-2">
            <span>
              {{t
                "accounts.loan_detail.original_label"
                amount=(this.money this.totalOriginalBalance)
              }}
            </span>
            <span>
              {{t "accounts.loan_detail.remaining_label" amount=(this.money this.totalBalance)}}
            </span>
          </div>
        </:body>
      </Section>

      <Section data-test-loan-account-detail-loans>
        <:header>{{t "accounts.loan_detail.loans"}}</:header>
        <:body>
          {{#if this.activeLoans.length}}
            <VStack class="gap-4">
              {{#each this.activeLoans as |loan|}}
                <Card @padding="md" @radius="card" data-test-loan-account-detail-loan>
                  <div class="tio-row-start-between gap-4">
                    <div>
                      <Heading @level={{3}}>{{this.loanTypeName loan}}</Heading>
                      {{#if loan.number}}
                        <Text @variant="muted" @size="sm">
                          {{t "accounts.loan_detail.masked_number" last4=(lastFour loan.number)}}
                        </Text>
                      {{/if}}
                    </div>
                    <div class="text-right">
                      <Text @size="lg" @weight="semibold">{{this.money loan.currentBalance}}</Text>
                      <Text @variant="muted" @size="sm">
                        {{t
                          "accounts.loan_detail.apr"
                          rate=(this.formatRate (loanInterestRate loan))
                        }}
                      </Text>
                    </div>
                  </div>

                  <div class="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    <Card
                      @radius="nested"
                      @padding="sm"
                      role="group"
                      aria-label={{t "accounts.loan_detail.original"}}
                    >
                      <div class="tio-text-overline tio-fg-muted">{{t
                          "accounts.loan_detail.original"
                        }}</div>
                      <div class="tio-text-sm-medium tio-fg-primary">{{this.money
                          loan.originalAmount
                        }}</div>
                    </Card>
                    <Card
                      @radius="nested"
                      @padding="sm"
                      role="group"
                      aria-label={{t "accounts.principal"}}
                    >
                      <div class="tio-text-overline tio-fg-muted">{{t "accounts.principal"}}</div>
                      <div class="tio-text-sm-medium tio-fg-primary">{{this.money
                          (principalBalance loan)
                        }}</div>
                    </Card>
                    <Card
                      @radius="nested"
                      @padding="sm"
                      role="group"
                      aria-label={{t "accounts.loan_detail.interest"}}
                    >
                      <div class="tio-text-overline tio-fg-muted">{{t
                          "accounts.loan_detail.interest"
                        }}</div>
                      <div class="tio-text-sm-medium tio-fg-primary">{{this.money
                          (interestBalance loan)
                        }}</div>
                    </Card>
                    <Card
                      @radius="nested"
                      @padding="sm"
                      role="group"
                      aria-label={{t "accounts.loan_detail.loan_type"}}
                    >
                      <div class="tio-text-overline tio-fg-muted">{{t
                          "accounts.loan_detail.loan_type"
                        }}</div>
                      <div class="tio-text-sm-medium tio-fg-primary truncate">{{this.loanClassLabel
                          loan
                        }}</div>
                    </Card>
                  </div>

                  {{#if loan.originalAmount}}
                    <div class="mt-3">
                      <div
                        class="w-full h-1.5 tio-bg-track rounded-full overflow-hidden flex"
                        role="progressbar"
                        aria-valuenow={{loanProgressPercent loan}}
                        aria-valuemin={{0}}
                        aria-valuemax={{100}}
                        aria-label={{t "accounts.loan_detail.paid_off"}}
                      >
                        <div
                          class="h-full app-progress-fill--brand"
                          {{style width=(concat (loanProgressPercent loan) "%")}}
                        ></div>
                      </div>
                      <Text @variant="muted" @size="xs" class="mt-1">
                        {{t
                          "accounts.loan_detail.loan_paid_off"
                          percent=(loanProgressPercent loan)
                        }}
                      </Text>
                    </div>
                  {{/if}}
                </Card>
              {{/each}}
            </VStack>
          {{else}}
            <Text @variant="muted" data-test-loan-account-detail-empty>
              {{t "accounts.loan_detail.no_loans"}}
            </Text>
          {{/if}}
        </:body>
      </Section>

      <Section data-test-loan-account-detail-next-steps>
        <:header>{{t "accounts.loan_detail.next_steps"}}</:header>
        <:body>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
            {{#if this.isReceivingContributions}}
              <NavCard
                @layout="horizontal"
                @route="authenticated.slr.dashboard"
                @icon={{PhHandshake}}
                @title={{t "accounts.loan_detail.contribution_history"}}
                @subtitle={{t "accounts.loan_detail.contribution_history_subtitle"}}
                data-test-loan-account-detail-contribution-history
              />
            {{/if}}
            <NavCard
              @layout="horizontal"
              @route="authenticated.repaying-student-debt.repayment-strategy-finder"
              @icon={{PhTarget}}
              @title={{t "sidebar.strategy_finder"}}
              @subtitle={{t "accounts.loan_detail.strategy_finder_subtitle"}}
            />
            <NavCard
              @layout="horizontal"
              @route="authenticated.observability.upload"
              @query={{hash
                source="loan-detail"
                providers="nslds,statement"
                returnTo="authenticated.account-activity.accounts"
              }}
              @icon={{PhCloudArrowUp}}
              @title={{t "accounts.loan_detail.upload_documents"}}
              @subtitle={{t "accounts.loan_detail.upload_documents_subtitle"}}
            />
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
